import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthHeaders, handleAuthFailure } from './AuthGate';
import { searchQueries, exportAllSessions } from '../api';
import type { QuerySearchResult } from '../types';

/* ── Types ─────────────────────────────────────────────────── */

interface Session {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

type SearchMode = 'sessions' | 'queries';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentSessionId: string | null;
  onLoadSession: (sessionId: string) => void;
  onNewChat: () => void;
  userId: string;
  onExportSession?: () => void;
  hasMessages?: boolean;
  /** Called to load a bookmarked or searched query by its queryId */
  onLoadQuery?: (queryId: string) => void;
  /** Opens the bookmarks panel */
  onOpenBookmarks?: () => void;
  bookmarkCount?: number;
}

/* ── Helpers ─────────────────────────────────────────────────── */

const API_BASE = import.meta.env.VITE_API_URL || '';
const SESSION_LIMIT = 200;

/* ── Inline styles ───────────────────────────────────────────── */

const searchWrapStyle: React.CSSProperties = {
  padding: '0 12px 8px',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--border, #334155)',
  background: 'var(--bg-2, #1e293b)',
  color: 'var(--ink-1, #f1f5f9)',
  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
  fontSize: 'var(--t-sm, 13px)',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const noResultsStyle: React.CSSProperties = {
  padding: '16px 12px',
  textAlign: 'center' as const,
  color: 'var(--ink-3, #94a3b8)',
  fontSize: 'var(--t-sm, 13px)',
  fontStyle: 'italic',
};

const modeToggleWrapStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
  padding: '0 12px 8px',
};

const modeToggleBtnBase: React.CSSProperties = {
  flex: 1,
  padding: '6px 8px',
  borderRadius: '6px',
  border: '1px solid var(--border, #334155)',
  background: 'transparent',
  color: 'var(--ink-3, #94a3b8)',
  fontFamily: 'inherit',
  fontSize: 'var(--t-sm, 13px)',
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
};

const modeToggleBtnActive: React.CSSProperties = {
  ...modeToggleBtnBase,
  background: 'var(--accent-soft, rgba(99, 102, 241, 0.12))',
  color: 'var(--accent-text, #9fa0f8)',
  borderColor: 'var(--accent, #6366f1)',
};

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + 'Z').getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr + 'Z').toLocaleDateString();
}

/* ── Component ───────────────────────────────────────────────── */

const exportBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  marginBottom: '4px',
  border: '1px solid var(--border, #334155)',
  borderRadius: '6px',
  background: 'transparent',
  color: 'var(--ink-2, #cbd5e1)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'var(--t-sm, 13px)',
  textAlign: 'left' as const,
};

const bookmarksBtnStyle: React.CSSProperties = {
  ...exportBtnStyle,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

export default function SessionSidebar({
  isOpen,
  onClose,
  currentSessionId,
  onLoadSession,
  onNewChat,
  userId,
  onExportSession,
  hasMessages = false,
  onLoadQuery,
  onOpenBookmarks,
  bookmarkCount = 0,
}: Props) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('sessions');
  const [queryResults, setQueryResults] = useState<QuerySearchResult[]>([]);
  const [querySearchLoading, setQuerySearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Focus search input when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 150);
    } else {
      setSearchQuery('');
      setDebouncedQuery('');
      setSearchMode('sessions');
      setQueryResults([]);
    }
  }, [isOpen]);

  // Fetch sessions when opened — bumped to 200 limit
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetch(`${API_BASE}/api/sessions?user_id=${encodeURIComponent(userId)}&limit=${SESSION_LIMIT}`, {
      headers: getAuthHeaders(),
    })
      .then((r) => {
        if (r.status === 401) { handleAuthFailure(); return { sessions: [] }; }
        return r.json();
      })
      .then((data: { sessions: Session[] }) => {
        setSessions(data.sessions ?? []);
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [isOpen, userId]);

  // Full-text query search (server-side) when in queries mode
  useEffect(() => {
    if (searchMode !== 'queries' || !debouncedQuery.trim()) {
      setQueryResults([]);
      return;
    }
    setQuerySearchLoading(true);
    searchQueries(debouncedQuery.trim(), 20)
      .then((results) => setQueryResults(results))
      .catch(() => setQueryResults([]))
      .finally(() => setQuerySearchLoading(false));
  }, [searchMode, debouncedQuery]);

  // Filter sessions by search query (title + content match)
  const filteredSessions = useMemo(() => {
    if (!debouncedQuery.trim()) return sessions;
    const q = debouncedQuery.toLowerCase();
    return sessions.filter((s) => {
      if ((s.title || '').toLowerCase().includes(q)) return true;
      const words = q.split(/\s+/);
      return words.every((w) => (s.title || '').toLowerCase().includes(w));
    });
  }, [sessions, debouncedQuery]);

  const handleDelete = useCallback(
    async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      try {
        await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      } catch {
        /* ignore */
      }
    },
    [],
  );

  const searchPlaceholder = searchMode === 'sessions'
    ? 'Search history…'
    : 'Search all queries…';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`sidebar-backdrop ${isOpen ? 'sidebar-backdrop--visible' : ''}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />

      {/* Sidebar */}
      <aside
        className={`session-sidebar ${isOpen ? 'session-sidebar--open' : ''}`}
        aria-label="Chat history"
      >
        <div className="sidebar-header">
          <h2 className="sidebar-title">History</h2>
          <button className="sidebar-close" onClick={onClose} aria-label="Close history">
            ✕
          </button>
        </div>

        <button className="sidebar-new-chat" onClick={() => { onNewChat(); onClose(); }}>
          + New Chat
        </button>

        {/* Export current session */}
        {hasMessages && onExportSession && (
          <div style={{ padding: '0 12px 4px' }}>
            <button
              style={exportBtnStyle}
              onClick={() => { onExportSession(); }}
              aria-label="Export current session as PDF"
              title="Export all messages and citations as a printable PDF"
            >
              Export session
            </button>
          </div>
        )}

        {/* G1: Export all sessions */}
        <div style={{ padding: '0 12px 4px' }}>
          <button
            style={exportBtnStyle}
            onClick={async () => {
              try {
                await exportAllSessions();
              } catch {
                /* ignore */
              }
            }}
            aria-label="Export all sessions as JSON"
            title="Download all your sessions and messages as a JSON file"
          >
            ⬇ Export All Sessions
          </button>
        </div>

        {/* Bookmarks button */}
        {onOpenBookmarks && (
          <div style={{ padding: '0 12px 4px' }}>
            <button
              style={bookmarksBtnStyle}
              onClick={() => { onOpenBookmarks(); onClose(); }}
              aria-label="View bookmarks"
              title="View saved bookmarks"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={1} aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Bookmarks
              {bookmarkCount > 0 && (
                <span style={{
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-text)',
                  borderRadius: 'var(--r-full)',
                  padding: '1px 7px',
                  fontSize: '11px',
                  fontWeight: 600,
                  marginLeft: 'auto',
                }}>
                  {bookmarkCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Search mode toggle */}
        <div style={modeToggleWrapStyle}>
          <button
            style={searchMode === 'sessions' ? modeToggleBtnActive : modeToggleBtnBase}
            onClick={() => setSearchMode('sessions')}
            aria-pressed={searchMode === 'sessions'}
          >
            Sessions
          </button>
          <button
            style={searchMode === 'queries' ? modeToggleBtnActive : modeToggleBtnBase}
            onClick={() => setSearchMode('queries')}
            aria-pressed={searchMode === 'queries'}
          >
            All Queries
          </button>
        </div>

        {/* Search input */}
        <div style={searchWrapStyle}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={searchInputStyle}
            aria-label={searchPlaceholder}
          />
        </div>

        {/* Content area */}
        <div className="sidebar-list">
          {searchMode === 'sessions' ? (
            /* ── Sessions mode ── */
            <>
              {loading && <div className="sidebar-loading">Loading…</div>}
              {!loading && sessions.length === 0 && (
                <div className="sidebar-empty">No previous chats</div>
              )}
              {!loading && sessions.length > 0 && filteredSessions.length === 0 && debouncedQuery && (
                <div style={noResultsStyle}>No matching sessions</div>
              )}
              {filteredSessions.map((s) => (
                <div
                  key={s.id}
                  className={`sidebar-item ${s.id === currentSessionId ? 'sidebar-item--active' : ''}`}
                  onClick={() => { onLoadSession(s.id); onClose(); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') { onLoadSession(s.id); onClose(); } }}
                >
                  <div className="sidebar-item-title">{s.title || 'Untitled'}</div>
                  <div className="sidebar-item-meta">
                    <span>{s.message_count} msg{s.message_count !== 1 ? 's' : ''}</span>
                    <span>{relativeTime(s.updated_at)}</span>
                  </div>
                  <button
                    className="sidebar-item-delete"
                    onClick={(e) => handleDelete(e, s.id)}
                    aria-label="Delete chat"
                    title="Delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              ))}
            </>
          ) : (
            /* ── All Queries mode ── */
            <>
              {!debouncedQuery.trim() && (
                <div style={noResultsStyle}>
                  Type to search across all your past queries and responses
                </div>
              )}
              {debouncedQuery.trim() && querySearchLoading && (
                <div className="sidebar-loading">Searching…</div>
              )}
              {debouncedQuery.trim() && !querySearchLoading && queryResults.length === 0 && (
                <div style={noResultsStyle}>No results found</div>
              )}
              {queryResults.map((r) => (
                <div
                  key={r.query_id}
                  className="sidebar-item query-search-result"
                  onClick={() => {
                    if (r.session_id) {
                      onLoadSession(r.session_id);
                    } else if (onLoadQuery) {
                      onLoadQuery(r.query_id);
                    }
                    onClose();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (r.session_id) onLoadSession(r.session_id);
                      else if (onLoadQuery) onLoadQuery(r.query_id);
                      onClose();
                    }
                  }}
                >
                  <div className="sidebar-item-title query-result-title">{r.query_text}</div>
                  <div className="query-result-preview">{r.response_preview}</div>
                  <div className="sidebar-item-meta">
                    {r.standard_refs.length > 0 && (
                      <span className="query-result-standards">
                        {r.standard_refs.slice(0, 2).join(', ')}
                        {r.standard_refs.length > 2 && ` +${r.standard_refs.length - 2}`}
                      </span>
                    )}
                    <span>{relativeTime(r.created_at)}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
